/**
 * Minimal JWT payload decode (FE-side, no signature verification).
 *
 * The access_token is issued by the MD-22 AUTH worker and verified on
 * every API request server-side. Client merely needs to read the
 * unverified payload to render UI (avatar, name, email). We treat the
 * payload as untrusted for any security-sensitive decision.
 *
 * Returns null on any parse failure - caller renders the safe fallback.
 */

export type JwtProfile = {
  sub?: string;
  email?: string;
  name?: string;
  picture?: string;
  exp?: number;
  iat?: number;
  tier?: 'anon' | 'authed' | 'pro' | 'admin';
};

function base64UrlDecode(input: string): string {
  // Replace URL-safe chars + pad to length % 4 == 0.
  let s = input.replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4 !== 0) s += '=';
  // atob is sufficient - JWT payload is short (< 4 KB).
  const binary = atob(s);
  // Decode UTF-8 (Vietnamese names contain multi-byte chars).
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder('utf-8').decode(bytes);
}

export function decodeJwtPayload(token: string | null | undefined): JwtProfile | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const json = base64UrlDecode(parts[1]);
    const obj = JSON.parse(json) as JwtProfile;
    return typeof obj === 'object' && obj !== null ? obj : null;
  } catch {
    return null;
  }
}
